import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { captureMessage } from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { useGetDetailedExtractionStatsByPeriodId } from '../../../../../services/funds.service';
import Button from '../../../../../components/Button';
import {
  useAutomateReExtraction,
  useCancelAutomateExtraction
} from '../../../../../services/automation.service';
import toast from 'react-hot-toast';
import Table from '../../../../../components/Table';
import Modal from 'react-modal';
import { TbReload, TbSearch } from 'react-icons/tb';
import { IoClose } from 'react-icons/io5';
import Checkbox from '../../../../../components/Checkbox';
import { GoArrowUpRight } from 'react-icons/go';
import { Tooltip } from 'react-tooltip';
import {
  CloseStyle,
  Container,
  TableContainerStyle,
  Title,
  ErrorMessageStyle,
  DescriptionStyle,
  FileNameStyle,
  ProgressCellStyle,
  ProgressTrackStyle,
  ProgressFillStyle,
  ProgressTextStyle,
  ConfidenceButtonStyle,
  DetailsModalBodyStyle,
  DetailsHeaderStyle,
  ScoreRingStyle,
  StatusPillStyle,
  DetailGridStyle,
  DetailCardStyle,
  SectionHeaderStyle,
  VerdictSummaryStyle,
  VerdictPillStyle,
  EvidenceFilterMetaStyle,
  EvidenceListStyle,
  EvidenceItemStyle,
  EvidenceHeaderStyle,
  EvidenceBadgeGroupStyle,
  EvidenceCategoryBadgeStyle,
  FieldConfidenceBadgeStyle,
  EvidenceValueStyle,
  EvidenceReasonStyle,
  SourceReferenceStyle,
  SourceMatchedValueStyle,
  SourceTableStyle,
  SourceTableCellStyle,
  NoteItemStyle,
  FooterStyle,
  customStyles,
  detailsModalStyles,
  colors
} from './styles';
import agentStore from '../../../../../store/agent';
import { PermissionGuard } from '../../../../../components/PermissionGuard';
import { WEB_PAGE_STATE_ENUMS } from '../../../../../components/ChatWidget/constants';
import { useDebouncedValue, useDidUpdate } from '@mantine/hooks';
import { Flex, Text, TextInput } from '@mantine/core';
import { SortButton } from '../../../components/SortButton';
import { FilterDropdown } from '../../../components/ProjectFilters/FilterDropdown';
import { TRANSFORMATION_TYPES } from '../../../../../consts/transformationTypes';
import { useSignalRListener } from '../../../../../hooks/useSignalR';
import { SIGNALR_LISTENERS } from '../../../../../config/signalr';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import { idCreator } from '../../../utils/idCreator';

const IN_PROGRESS_START_PERCENTAGE = 10;
const IN_PROGRESS_MAX_PERCENTAGE = 95;
const PROGRESS_STORAGE_KEY_PREFIX = 'extraction-progress-start';

const PROGRESS_PHASES = {
  fullEstimate: { min: 10, max: 95, weight: 1 },
  headerFooter: { min: 25, max: 45, weight: 0.2 },
  fundMapping: { min: 45, max: 70, weight: 0.25 },
  extraction: { min: 70, max: 95, weight: 0.35 }
};

export const ExtractionIssuesModal = ({
  isOpen,
  onClose,
  periodId,
  worklowId,
  refetch,
  isLatestPeriod,
  canManageWorkflow = true
}) => {
  const navigate = useNavigate();
  const automateReExtraction = useAutomateReExtraction();
  const cancelAutomateExtraction = useCancelAutomateExtraction();

  const [selectedExtraction, setSeletectedExtraction] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedConfidenceRecord, setSelectedConfidenceRecord] = useState(null);
  const [selectedEvidenceVerdict, setSelectedEvidenceVerdict] = useState(null);
  const [resettingConfidenceBySheet, setResettingConfidenceBySheet] = useState({});
  const [progressNow, setProgressNow] = useState(Date.now());
  const categoryOptionsRef = useRef([]);
  const progressStartTimeByTaskRef = useRef({});

  const apiParams = useMemo(() => {
    const p = {};
    if (debouncedSearch) p.search_query = debouncedSearch;
    if (selectedCategory) p.category_name = selectedCategory.name;
    if (sortConfig.key) p.sort_by = sortConfig.direction;
    return p;
  }, [debouncedSearch, selectedCategory, sortConfig]);

  const {
    refetch: refetchIssues,
    data: issues,
    isLoading
  } = useGetDetailedExtractionStatsByPeriodId({
    periodId,
    params: apiParams,
    queryParams: {
      enabled: !!periodId && isOpen
      // refetchInterval: 5000 // Disabled polling - using SignalR real-time updates instead
    }
  });

  const hasCompletedRowsPendingConfidence = useMemo(() => {
    return (issues ?? []).some(record => {
      return (
        record.latest_automation_task?.status === 'Completed' &&
        Boolean(record.extraction_id) &&
        record.automation_extraction_type !== TRANSFORMATION_TYPES.MANUAL &&
        typeof record.confidence_score !== 'number'
      );
    });
  }, [issues]);

  useEffect(() => {
    if (!isOpen || !hasCompletedRowsPendingConfidence) return undefined;

    const intervalId = setInterval(() => {
      refetchIssues();
    }, 2000);

    return () => clearInterval(intervalId);
  }, [hasCompletedRowsPendingConfidence, isOpen, refetchIssues]);

  useSignalRListener(SIGNALR_LISTENERS.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED, data => {
    // TODO: Once confirmed that extraction status update works remove this sentry log
    captureMessage('SignalR ON_AUTOMATION_EXTRACTION_STATUS_UPDATED', {
      tags: {
        service: 'signalr-service'
      },
      extra: {
        dataFromBackend: data,
        dataFromFrontend: {
          isOpen,
          periodId
        }
      }
    });
    console.log('ExtractionStatusUpdate received:', data, periodId, isOpen);

    // Check if the notification is for the current period
    // Environment filtering is handled automatically by useSignalRListener hook
    if (data.period_id === periodId && isOpen) {
      console.log('Period match - refetching extraction issues');
      refetchIssues();
    }
  });

  const onAutoExtract = () => {
    const resettingRows = selectedExtraction.reduce((acc, sheetMappingId) => {
      const record = (issues ?? []).find(item => item.sheet_mapping_id === sheetMappingId);
      acc[sheetMappingId] = record?.latest_automation_task?.id ?? null;
      return acc;
    }, {});
    setResettingConfidenceBySheet(prev => ({ ...prev, ...resettingRows }));

    automateReExtraction.mutate(
      {
        period_id: periodId,
        sheet_mapping_ids: selectedExtraction
      },
      {
        onSuccess: () => {
          setSeletectedExtraction([]);
          refetch();
          refetchIssues();
          toast.success('Automated Extraction have been successfully applied', {
            position: 'bottom-center'
          });
        }
      }
    );
  };

  const onCancelExtraction = () => {
    cancelAutomateExtraction.mutate(
      {
        task_ids: selectedExtraction
          .map(
            item =>
              (issues ?? []).find(value => value.sheet_mapping_id === item)?.latest_automation_task
                ?.id
          )
          .filter(Boolean)
      },
      {
        onSuccess: () => {
          setSeletectedExtraction([]);
          refetch();
          refetchIssues();
          toast.success('Automated Extraction has been successfully cancelled', {
            position: 'bottom-center'
          });
        }
      }
    );
  };

  const onSelectExtraction = id => {
    if (selectedExtraction.includes(id)) {
      setSeletectedExtraction(prev => prev.filter(value => value !== id));
    } else {
      setSeletectedExtraction(prev => [...prev, id]);
    }
  };

  const onNavigate = (fileId, sheetId, category, extractionId) => {
    navigate(
      `/data-extraction/${worklowId}/${periodId}?fileId=${fileId}&sheetId=${sheetId}${category ? `&category=${category}` : ''}${extractionId ? `&extractId=${extractionId}` : ''}`
    );
  };

  const isAllSelected = useMemo(() => {
    const selectable = issues?.filter(
      item => item?.automation_extraction_type !== TRANSFORMATION_TYPES.MANUAL
    );
    if (!selectable?.length) return false;
    return selectable.every(item => selectedExtraction.includes(item.sheet_mapping_id));
  }, [selectedExtraction, issues]);

  const toggleAllExtractions = () => {
    if (isAllSelected) {
      setSeletectedExtraction([]);
    } else {
      setSeletectedExtraction(
        issues
          .filter(item => item.automation_extraction_type !== TRANSFORMATION_TYPES.MANUAL)
          .map(item => item.sheet_mapping_id)
      );
    }
  };

  const categoryOptions = useMemo(() => {
    if (!issues?.length) {
      categoryOptionsRef.current = [];
      return categoryOptionsRef.current;
    }
    if (!selectedCategory) {
      const unique = [...new Set(issues.map(item => item.category).filter(Boolean))];
      categoryOptionsRef.current = unique
        .sort()
        .map(category => ({ id: category, name: category }));
    }
    return categoryOptionsRef.current;
  }, [issues, selectedCategory]);

  const tableData = issues ?? [];

  useEffect(() => {
    const inProgressTaskIds = new Set();
    const now = Date.now();

    (issues ?? []).forEach(record => {
      const task = record.latest_automation_task;
      if (task?.status !== 'In progress' || task.id == null) return;

      const taskId = String(task.id);
      inProgressTaskIds.add(taskId);

      if (!progressStartTimeByTaskRef.current[taskId]) {
        const storageKey = `${PROGRESS_STORAGE_KEY_PREFIX}-${taskId}`;
        const storedTimestamp = Number(window.sessionStorage.getItem(storageKey));
        const startTimestamp = storedTimestamp || now;

        progressStartTimeByTaskRef.current[taskId] = startTimestamp;
        window.sessionStorage.setItem(storageKey, String(startTimestamp));
      }
    });

    Object.keys(progressStartTimeByTaskRef.current).forEach(taskId => {
      if (!inProgressTaskIds.has(taskId)) {
        delete progressStartTimeByTaskRef.current[taskId];
        window.sessionStorage.removeItem(`${PROGRESS_STORAGE_KEY_PREFIX}-${taskId}`);
        Object.keys(PROGRESS_PHASES).forEach(phase => {
          window.sessionStorage.removeItem(`${PROGRESS_STORAGE_KEY_PREFIX}-${taskId}-${phase}`);
        });
      }
    });

    if (!isOpen || inProgressTaskIds.size === 0) return undefined;

    setProgressNow(now);
    const intervalId = window.setInterval(() => {
      setProgressNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isOpen, issues]);

  const getTaskTimestamp = task => {
    const timestamp = task?.modified_at || task?.updated_at;
    const parsedTimestamp = timestamp ? Date.parse(timestamp) : NaN;
    return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp;
  };

  const getTaskPhase = task => {
    if (task?.step === 'HEADER_AND_FOOTER_INDEXES') return 'headerFooter';
    if (task?.step === 'Fund mapping') return 'fundMapping';
    if (task?.step === 'Extraction') return 'extraction';
    return 'fullEstimate';
  };

  const getDeterministicProgressFactor = record => {
    const seed = String(
      record.sheet_mapping_id || record.sheet_id || record.latest_automation_task?.id || ''
    )
      .split('')
      .reduce((sum, character) => sum + character.charCodeAt(0), 0);
    return 0.85 + (seed % 40) / 100;
  };

  const getEstimatedTaskDuration = record => {
    let duration = 2 * 60 * 1000;
    if (!record.is_structured) duration *= 1.8;

    const category = String(record.category || '').toLowerCase();
    if (category.includes('lookup')) duration *= 0.65;
    if (category.includes('shareholder')) duration *= 0.85;
    if (category.includes('turnover')) duration *= 1.15;
    if (category.includes('exposure')) duration *= 1.35;
    if (category.includes('operational')) duration *= 1.1;

    return duration * getDeterministicProgressFactor(record);
  };

  const getPhaseStartTime = (task, phase) => {
    const taskId = task?.id == null ? null : String(task.id);
    if (!taskId) return null;

    const timestamp = phase === 'fullEstimate' ? null : getTaskTimestamp(task);
    const storageKey = `${PROGRESS_STORAGE_KEY_PREFIX}-${taskId}-${phase}`;
    const storedValue = window.sessionStorage.getItem(storageKey);
    const storedTimestamp = storedValue ? Number(storedValue) : null;
    const startTime =
      timestamp ?? storedTimestamp ?? progressStartTimeByTaskRef.current[taskId] ?? progressNow;
    if (storedValue == null && startTime != null) {
      window.sessionStorage.setItem(storageKey, String(startTime));
    }
    return startTime;
  };

  const getEstimatedProgressPercentage = record => {
    const task = record.latest_automation_task;
    const phaseKey = getTaskPhase(task);
    const phase = PROGRESS_PHASES[phaseKey];
    const phaseStartTime = getPhaseStartTime(task, phaseKey);
    if (!phaseStartTime) return IN_PROGRESS_START_PERCENTAGE;

    const phaseDuration = getEstimatedTaskDuration(record) * phase.weight;
    const phaseRatio = Math.min(Math.max((progressNow - phaseStartTime) / phaseDuration, 0), 1);
    return Math.round(phase.min + phaseRatio * (phase.max - phase.min));
  };

  const getProgressPercentage = record => {
    const backendPercentage = record.latest_automation_task?.progress_percentage;
    const task = record.latest_automation_task;
    if (typeof backendPercentage === 'number') {
      const normalizedBackendPercentage = Math.min(100, Math.max(0, Math.round(backendPercentage)));
      if (task?.status !== 'In progress') return normalizedBackendPercentage;

      const estimatedPercentage = getEstimatedProgressPercentage(record);

      return Math.min(
        IN_PROGRESS_MAX_PERCENTAGE,
        Math.max(normalizedBackendPercentage, estimatedPercentage)
      );
    }

    const status = task?.status;
    if (status === 'Completed') return 100;
    if (status === 'In progress') {
      return Math.min(IN_PROGRESS_MAX_PERCENTAGE, getEstimatedProgressPercentage(record));
    }
    if (status === 'Failed' || status === 'Cancelled' || status === 'Not started') return 0;
    return 0;
  };

  const getConfidenceScore = record => {
    const taskStatus = record.latest_automation_task?.status;
    if (['Failed', 'Cancelled', 'Not started', 'In progress'].includes(taskStatus)) return null;

    const confidenceScore = record.confidence_score;
    if (typeof confidenceScore !== 'number') return null;
    return Math.min(100, Math.max(0, confidenceScore));
  };

  const verdictTones = {
    matched: { color: '#2F9E44', background: '#EBFBEE', border: '#B2F2BB' },
    uncertain: { color: '#F08C00', background: '#FFF4E6', border: '#FFD8A8' },
    mismatch: { color: '#E03131', background: '#FFF5F5', border: '#FFC9C9' },
    missing_source: { color: '#5C5F66', background: '#F1F3F5', border: '#DEE2E6' },
    verified: { color: '#2F9E44', background: '#EBFBEE', border: '#B2F2BB' },
    needs_review: { color: '#F08C00', background: '#FFF4E6', border: '#FFD8A8' },
    unverified: { color: '#5C5F66', background: '#F1F3F5', border: '#DEE2E6' }
  };

  const getScoreColor = score => {
    if (score >= 85) return '#2F9E44';
    if (score >= 70) return '#F08C00';
    return '#E03131';
  };

  const formatSourceReference = sourceReference => {
    if (!sourceReference) return '-';
    const sourceText = String(sourceReference);

    const xyMatch = sourceText.match(/^x:\s*(\d+),\s*y:\s*(\d+)$/i);
    if (xyMatch) {
      const [, column, row] = xyMatch;
      return `Row ${Number(row) + 1}, Column ${Number(column) + 1}`;
    }

    const bareCoordinatesMatch = sourceText.match(/^(\d+)\s*,\s*(\d+)$/);
    if (bareCoordinatesMatch) {
      const [, column, row] = bareCoordinatesMatch;
      return `Row ${Number(row) + 1}, Column ${Number(column) + 1}`;
    }

    const parenthesizedCoordinatesMatch = sourceText.match(/^\((\d+)\s*,\s*(\d+)\)$/);
    if (parenthesizedCoordinatesMatch) {
      const [, column, row] = parenthesizedCoordinatesMatch;
      return `Row ${Number(row) + 1}, Column ${Number(column) + 1}`;
    }

    const rowColumnMatch = sourceText.match(/^row\s*(\d+)\s*,\s*col(?:umn)?\s*:?\s*(\d+)$/i);
    if (rowColumnMatch) {
      const [, row, column] = rowColumnMatch;
      return `Row ${Number(row)}, Column ${Number(column)}`;
    }

    const valueAtCoordinatesMatch = sourceText.match(/^(.*)\s+at\s+\((\d+),\s*(\d+)\)$/i);
    if (valueAtCoordinatesMatch) {
      const [, value, column, row] = valueAtCoordinatesMatch;
      return `${value} at Row ${Number(row) + 1}, Column ${Number(column) + 1}`;
    }

    return sourceText
      .replace(/source_row_index:\s*(\d+)/gi, (_, row) => `Row ${Number(row) + 1}`)
      .replace(/\bx:\s*(\d+)\b/gi, (_, column) => `Column ${Number(column) + 1}`)
      .replace(/\by:\s*(\d+)\b/gi, (_, row) => `Row ${Number(row) + 1}`);
  };

  const parseCoordinateSourceReference = (sourceReference, extractedValue) => {
    if (!sourceReference) return null;
    const sourceText = String(sourceReference).trim();
    const xyMatch = sourceText.match(/^x:\s*(\d+),\s*y:\s*(\d+)$/i);
    const bareCoordinatesMatch = sourceText.match(/^(\d+)\s*,\s*(\d+)$/);
    const parenthesizedCoordinatesMatch = sourceText.match(/^\((\d+)\s*,\s*(\d+)\)$/);
    const rowColumnMatch = sourceText.match(/^row\s*(\d+)\s*,\s*col(?:umn)?\s*:?\s*(\d+)$/i);
    const coordinateMatch = xyMatch || bareCoordinatesMatch || parenthesizedCoordinatesMatch;

    if (rowColumnMatch) {
      const [, row, column] = rowColumnMatch;
      return {
        rowNumber: Number(row),
        columnNumber: Number(column),
        sourceValue: extractedValue || '-'
      };
    }

    if (!coordinateMatch) return null;

    const [, column, row] = coordinateMatch;
    return {
      rowNumber: Number(row) + 1,
      columnNumber: Number(column) + 1,
      sourceValue: extractedValue || '-'
    };
  };

  const parseCsvLine = line => {
    const values = [];
    let currentValue = '';
    let inQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
      const character = line[index];
      const nextCharacter = line[index + 1];

      if (character === '"' && nextCharacter === '"' && inQuotes) {
        currentValue += '"';
        index += 1;
      } else if (character === '"') {
        inQuotes = !inQuotes;
      } else if (character === ',' && !inQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += character;
      }
    }

    values.push(currentValue.trim());
    return values;
  };

  const normalizeComparableValue = value =>
    String(value ?? '')
      .trim()
      .replace(/,/g, '')
      .toLowerCase();

  const parseStructuredSourceReference = (sourceReference, extractedValue) => {
    if (!sourceReference) return null;
    const sourceText = String(sourceReference).trim();
    if (!sourceText.startsWith('source_row_index,')) return null;

    const lines = sourceText.split(/\r?\n/).filter(Boolean);
    let headerLine = lines[0];
    let rowLine = lines[1];

    if (!rowLine) {
      const singleLineMatch = sourceText.match(/^(source_row_index(?:,\d+)+)\s+(.+)$/);
      if (!singleLineMatch) return null;
      [, headerLine, rowLine] = singleLineMatch;
    }

    const headers = parseCsvLine(headerLine);
    const values = parseCsvLine(rowLine);
    if (headers[0] !== 'source_row_index' || values.length < 2) return null;

    const sourceRowNumber = Number(values[0]) + 1;
    const extractedComparableValue = normalizeComparableValue(extractedValue);
    const sourceCells = headers
      .slice(1)
      .map((header, index) => ({
        columnNumber: Number(header) + 1,
        value: values[index + 1]
      }))
      .filter(cell => cell.value && cell.value.toLowerCase() !== 'nan');

    const matchedCells = sourceCells.filter(cell => {
      const cellComparableValue = normalizeComparableValue(cell.value);
      if (cellComparableValue === extractedComparableValue) return true;

      const numericCellValue = Number(cellComparableValue);
      const numericExtractedValue = Number(extractedComparableValue);
      return (
        Number.isFinite(numericCellValue) &&
        Number.isFinite(numericExtractedValue) &&
        Math.abs(numericCellValue - numericExtractedValue) < 0.000001
      );
    });

    const visibleCells =
      matchedCells.length > 0
        ? [...matchedCells, ...sourceCells.filter(cell => !matchedCells.includes(cell)).slice(0, 5)]
        : sourceCells.slice(0, 8);

    return {
      sourceRowNumber,
      matchedCells,
      visibleCells
    };
  };

  const parseCoordinateListSourceReference = (sourceReference, extractedValue) => {
    if (!sourceReference) return null;
    const sourceText = String(sourceReference).trim();
    const rowMatch = sourceText.match(/\by\s*=\s*(\d+)\b/i);
    if (!rowMatch || !sourceText.match(/\bx\s*=\s*\d+/i)) return null;

    const sourceRowNumber = Number(rowMatch[1]) + 1;
    const sourceCells = [];
    const xValueRegex = /\bx\s*=\s*(\d+)\s+([^,;]+)/gi;
    let match = xValueRegex.exec(sourceText);

    while (match) {
      sourceCells.push({
        columnNumber: Number(match[1]) + 1,
        value: match[2].trim()
      });
      match = xValueRegex.exec(sourceText);
    }

    if (!sourceCells.length) return null;

    const extractedComparableValue = normalizeComparableValue(extractedValue);
    const matchedCells = sourceCells.filter(cell => {
      const cellComparableValue = normalizeComparableValue(cell.value);
      if (cellComparableValue && extractedComparableValue.includes(cellComparableValue))
        return true;

      const numericCellValue = Number(cellComparableValue);
      return (
        Number.isFinite(numericCellValue) &&
        extractedComparableValue.includes(String(numericCellValue))
      );
    });

    return {
      sourceRowNumber,
      matchedCells,
      visibleCells: sourceCells.slice(0, 10)
    };
  };

  const parseMultiColumnSourceReference = sourceReference => {
    if (!sourceReference) return null;
    const sourceText = String(sourceReference).trim();
    const match = sourceText.match(
      /^row\s+(\d+)\s*,\s*columns?\s+([\w\s/,-]+?)\s*,\s*source values?\s*:\s*(.+)$/i
    );
    if (!match) return null;

    const [, row, columnsText, valuesText] = match;
    const rawColumns = columnsText
      .split('/')
      .map(column => column.trim())
      .filter(Boolean);
    const values = valuesText
      .split(';')
      .map(value => value.trim())
      .filter(Boolean);
    const hasZeroBasedNumericColumn = rawColumns.some(column => Number(column) === 0);
    const visibleCells = rawColumns.map((column, index) => {
      const numericColumn = Number(column);
      const columnNumber = Number.isFinite(numericColumn)
        ? numericColumn + (hasZeroBasedNumericColumn ? 1 : 0)
        : column;

      return {
        columnNumber,
        value: values[index] ?? '-'
      };
    });

    return {
      sourceRowNumber: Number(row),
      matchedCells: visibleCells,
      visibleCells
    };
  };

  const renderStructuredSourceTable = (structuredSource, evidenceItemId) => (
    <SourceReferenceStyle id={`ai-extraction-evidence-source-structured-${evidenceItemId}`}>
      {structuredSource.matchedCells.length > 0 && (
        <SourceMatchedValueStyle id={`ai-extraction-evidence-source-match-${evidenceItemId}`}>
          Source value: {structuredSource.matchedCells[0].value} · Row{' '}
          {structuredSource.sourceRowNumber}, Column {structuredSource.matchedCells[0].columnNumber}
        </SourceMatchedValueStyle>
      )}
      <SourceTableStyle id={`ai-extraction-evidence-source-table-${evidenceItemId}`}>
        <SourceTableCellStyle
          id={`ai-extraction-evidence-source-row-header-${evidenceItemId}`}
          $header
        >
          Row
        </SourceTableCellStyle>
        <SourceTableCellStyle
          id={`ai-extraction-evidence-source-column-header-${evidenceItemId}`}
          $header
        >
          Column
        </SourceTableCellStyle>
        <SourceTableCellStyle
          id={`ai-extraction-evidence-source-value-header-${evidenceItemId}`}
          $header
        >
          Source value
        </SourceTableCellStyle>
        {structuredSource.visibleCells.map(cell => (
          <Fragment key={`${structuredSource.sourceRowNumber}-${cell.columnNumber}-${cell.value}`}>
            <SourceTableCellStyle
              id={`ai-extraction-evidence-source-row-${evidenceItemId}-${structuredSource.sourceRowNumber}-${cell.columnNumber}`}
            >
              {structuredSource.sourceRowNumber}
            </SourceTableCellStyle>
            <SourceTableCellStyle
              id={`ai-extraction-evidence-source-column-${evidenceItemId}-${structuredSource.sourceRowNumber}-${cell.columnNumber}`}
            >
              {cell.columnNumber}
            </SourceTableCellStyle>
            <SourceTableCellStyle
              id={`ai-extraction-evidence-source-value-${evidenceItemId}-${structuredSource.sourceRowNumber}-${cell.columnNumber}`}
            >
              {cell.value}
            </SourceTableCellStyle>
          </Fragment>
        ))}
      </SourceTableStyle>
    </SourceReferenceStyle>
  );

  const renderSourceReference = (item, evidenceItemId) => {
    const structuredSource = parseStructuredSourceReference(
      item.source_reference,
      item.extracted_value
    );
    const coordinateListSource = parseCoordinateListSourceReference(
      item.source_reference,
      item.extracted_value
    );
    const multiColumnSource = parseMultiColumnSourceReference(item.source_reference);
    const coordinateSource = parseCoordinateSourceReference(
      item.source_reference,
      item.extracted_value
    );
    if (coordinateSource) {
      return (
        <SourceReferenceStyle id={`ai-extraction-evidence-source-coordinate-${evidenceItemId}`}>
          <SourceMatchedValueStyle
            id={`ai-extraction-evidence-source-coordinate-match-${evidenceItemId}`}
          >
            Source value: {coordinateSource.sourceValue} · Row {coordinateSource.rowNumber}, Column{' '}
            {coordinateSource.columnNumber}
          </SourceMatchedValueStyle>
        </SourceReferenceStyle>
      );
    }

    if (coordinateListSource) {
      return renderStructuredSourceTable(coordinateListSource, evidenceItemId);
    }

    if (multiColumnSource) {
      return renderStructuredSourceTable(multiColumnSource, evidenceItemId);
    }

    if (!structuredSource) {
      return formatSourceReference(item.source_reference);
    }

    return renderStructuredSourceTable(structuredSource, evidenceItemId);
  };

  const getFieldConfidenceScore = item => {
    if (typeof item.confidence_score === 'number')
      return Math.min(100, Math.max(0, item.confidence_score));

    const verdictScores = {
      matched: 98,
      uncertain: 72,
      missing_source: 55,
      mismatch: 25
    };
    return verdictScores[item.verdict] ?? 55;
  };

  const getEvidenceItemId = item =>
    idCreator(
      [
        item.extraction_category ||
          selectedDetails.source?.category ||
          selectedConfidenceRecord?.category,
        item.field,
        item.source_reference,
        item.verdict
      ]
        .filter(Boolean)
        .join('-')
    );

  const sanitizeVerifierText = value => {
    if (!value) return '';
    const blockedPhrases = [
      'truncated',
      'json',
      'payload',
      'prompt',
      'coordinate',
      'coordinates',
      'schema',
      'compact extracted json',
      'provided source/extracted context',
      'provided source context',
      'provided context',
      'extraction payload shown',
      'later extracted sections',
      'later sections',
      'visible excerpt',
      'visible text'
    ];
    const sentences = String(value)
      .replace(/\n/g, ' ')
      .split('.')
      .map(sentence => sentence.trim())
      .filter(
        sentence =>
          sentence && !blockedPhrases.some(phrase => sentence.toLowerCase().includes(phrase))
      );

    return sentences.length ? `${sentences.join('. ')}.` : '';
  };

  const isMeaningfulEvidenceItem = item => {
    const field = String(item.field || '').toLowerCase();
    const extractedValue = String(item.extracted_value || '').toLowerCase();
    if (field.startsWith('completeness') || field.includes('full extraction after truncation'))
      return false;
    if (
      extractedValue.includes('not present in extracted json') ||
      extractedValue.includes('not fully available')
    ) {
      return false;
    }
    return true;
  };

  const selectedDetails = selectedConfidenceRecord?.ai_extraction_details ?? {};
  const selectedConfidenceScore = selectedConfidenceRecord
    ? getConfidenceScore(selectedConfidenceRecord)
    : null;
  const selectedEvidence = Array.isArray(selectedDetails.evidence)
    ? selectedDetails.evidence.filter(isMeaningfulEvidenceItem)
    : [];
  const selectedConcerns = Array.isArray(selectedDetails.concerns)
    ? selectedDetails.concerns.map(sanitizeVerifierText).filter(Boolean)
    : [];
  const selectedMethodology = Array.isArray(selectedDetails.methodology)
    ? selectedDetails.methodology.map(sanitizeVerifierText).filter(Boolean)
    : [];
  const displayedVerifierStatus =
    selectedEvidence.length > 0 &&
    Number(selectedDetails.needs_review_fields || 0) === 0 &&
    selectedEvidence.every(item => item.verdict === 'matched')
      ? 'verified'
      : selectedDetails.status;
  const selectedStatusTone = verdictTones[displayedVerifierStatus] || verdictTones.unverified;
  const selectedScoreColor = getScoreColor(selectedConfidenceScore ?? 0);
  const sortedEvidence = [...selectedEvidence].sort((left, right) => {
    const priority = { mismatch: 0, uncertain: 1, missing_source: 2, matched: 3 };
    return (priority[left.verdict] ?? 4) - (priority[right.verdict] ?? 4);
  });
  const filteredEvidence = selectedEvidenceVerdict
    ? sortedEvidence.filter(item => (item.verdict || 'missing_source') === selectedEvidenceVerdict)
    : sortedEvidence;
  const verdictCounts = selectedEvidence.reduce((acc, item) => {
    const verdict = item.verdict || 'missing_source';
    acc[verdict] = (acc[verdict] || 0) + 1;
    return acc;
  }, {});

  const columns = [
    {
      key: 'file_name',
      title: 'File name',
      renderHeadCell: () => {
        return (
          <FileNameStyle>
            {isLatestPeriod && (
              <Checkbox
                checked={isAllSelected}
                onChange={toggleAllExtractions}
                data-testid="extraction-checkbox-all"
              />
            )}
            File name
            <SortButton
              columnName="file_name"
              setSortConfig={setSortConfig}
              sortConfig={sortConfig}
            />
          </FileNameStyle>
        );
      },
      render: record => {
        const isManually = record?.automation_extraction_type === TRANSFORMATION_TYPES.MANUAL;
        return (
          <FileNameStyle>
            {isLatestPeriod && (
              <Checkbox
                disabled={isManually}
                checked={selectedExtraction.includes(record.sheet_mapping_id)}
                onChange={() => onSelectExtraction(record.sheet_mapping_id)}
                data-testid={`extraction-checkbox-${record.sheet_mapping_id}`}
              />
            )}
            {record.file_name}
          </FileNameStyle>
        );
      }
    },
    {
      key: 'sheet_name',
      title: 'Sheet name',
      renderHeadCell: () => (
        <Flex align="center" gap="4px">
          <Text fz="14px" lh="20px" c="#495057" fw="600">
            Sheet name
          </Text>
          <SortButton
            columnName="sheet_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </Flex>
      )
    },
    {
      key: 'table_structure',
      title: 'Table Structure',
      render: record =>
        record.is_structured == null ? '-' : record.is_structured ? 'Structured' : 'Unstructured'
    },
    {
      key: 'category',
      title: 'Category',
      renderHeadCell: () => (
        <Flex align="center" gap="4px">
          <Text fz="14px" lh="20px" c="#495057" fw="600">
            Category
          </Text>
          <SortButton
            columnName="category_name"
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        </Flex>
      )
    },
    {
      key: 'status',
      title: 'Status',
      renderHeadCell: () => (
        <Text fz="14px" lh="20px" c="#495057" fw="600">
          Status
        </Text>
      ),
      render: record => {
        const isManually = record?.automation_extraction_type === TRANSFORMATION_TYPES.MANUAL;

        return (
          <p
            style={{
              color: isManually
                ? colors['Not extracted']
                : colors[record.latest_automation_task?.status || 'Not extracted']
            }}
          >
            {!isManually && record.latest_automation_task
              ? record.latest_automation_task?.status
              : !isManually && 'Not extracted'}

            {isManually && TRANSFORMATION_TYPES.MANUAL}
          </p>
        );
      }
    },
    {
      key: 'progress_percentage',
      title: 'Progress',
      renderHeadCell: () => (
        <Text fz="14px" lh="20px" c="#495057" fw="600">
          Progress
        </Text>
      ),
      render: record => {
        const percentage = getProgressPercentage(record);
        if (percentage == null) return '-';

        return (
          <ProgressCellStyle
            id={`extraction-progress-${record.sheet_mapping_id || record.sheet_id}`}
          >
            <ProgressTrackStyle
              id={`extraction-progress-track-${record.sheet_mapping_id || record.sheet_id}`}
            >
              <ProgressFillStyle
                id={`extraction-progress-fill-${record.sheet_mapping_id || record.sheet_id}`}
                $percentage={percentage}
              />
            </ProgressTrackStyle>
            <ProgressTextStyle
              id={`extraction-progress-text-${record.sheet_mapping_id || record.sheet_id}`}
            >
              {percentage}%
            </ProgressTextStyle>
          </ProgressCellStyle>
        );
      }
    },
    {
      key: 'confidence_score',
      title: 'Confidence Score',
      renderHeadCell: () => (
        <Text fz="14px" lh="20px" c="#495057" fw="600">
          Confidence Score
        </Text>
      ),
      render: record => {
        const resetPreviousTaskId = resettingConfidenceBySheet[record.sheet_mapping_id];
        const isResettingConfidence = Object.prototype.hasOwnProperty.call(
          resettingConfidenceBySheet,
          record.sheet_mapping_id
        );
        const currentTask = record.latest_automation_task;
        const shouldHideConfidence =
          isResettingConfidence &&
          (currentTask?.id === resetPreviousTaskId ||
            currentTask?.status === 'In progress' ||
            typeof record.confidence_score !== 'number');
        const confidenceScore = shouldHideConfidence ? null : getConfidenceScore(record);
        const isVerificationPending =
          confidenceScore == null && record.latest_automation_task?.status === 'In progress';
        const isVerifierFinishing =
          confidenceScore == null &&
          currentTask?.status === 'Completed' &&
          record.automation_extraction_type !== TRANSFORMATION_TYPES.MANUAL &&
          Boolean(record.extraction_id);

        return (
          <ConfidenceButtonStyle
            id={`extraction-confidence-${record.sheet_mapping_id || record.sheet_id}`}
            type="button"
            $score={confidenceScore}
            disabled={confidenceScore == null || !record.ai_extraction_details}
            onClick={() => {
              setSelectedEvidenceVerdict(null);
              setSelectedConfidenceRecord(record);
            }}
          >
            {confidenceScore == null
              ? isVerificationPending
                ? 'Pending'
                : isVerifierFinishing
                  ? 'Calculating'
                  : '-'
              : `${confidenceScore.toFixed(1)}%`}
          </ConfidenceButtonStyle>
        );
      }
    },
    {
      key: 'issue_description',
      title: 'Issue Description',
      render: record => {
        const hasErrorAndWarning =
          record.latest_automation_task?.automation_extraction_task_warnings?.length > 0;

        return (
          <DescriptionStyle>
            {(record.latest_automation_task?.automation_extraction_task_warnings?.length > 0 ||
              record.latest_automation_task?.error_message) && (
              <>
                <ErrorMessageStyle
                  style={{
                    textDecoration:
                      record.latest_automation_task?.automation_extraction_task_warnings?.length >
                        0 && 'underline',
                    color:
                      record.latest_automation_task?.automation_extraction_task_warnings?.length >
                        0 && '#FD7E14'
                  }}
                  data-tooltip-id={
                    record.latest_automation_task?.automation_extraction_task_warnings?.length >
                      0 && 'warning-tooltip'
                  }
                  data-tooltip-content={record.latest_automation_task?.automation_extraction_task_warnings
                    ?.map((item, index) => `${index + 1}. ${item.message}`)
                    .join(`\n`)}
                  data-tooltip-place="left-start"
                >
                  {record.latest_automation_task?.automation_extraction_task_warnings?.length > 0
                    ? 'Warnings'
                    : record.latest_automation_task?.error_message}{' '}
                  <IoIosInformationCircleOutline cursor="pointer" style={{ flexShrink: 0 }} />
                </ErrorMessageStyle>

                <GoArrowUpRight
                  onClick={() =>
                    onNavigate(
                      record.file_id,
                      record.sheet_id,
                      record.category,
                      record.extraction_id
                    )
                  }
                  color="#228BE6"
                  style={{
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                />
              </>
            )}

            {(!record.latest_automation_task ||
              (record.latest_automation_task?.status === 'Completed' && !hasErrorAndWarning)) && (
              <>
                <div />
                <GoArrowUpRight
                  onClick={() =>
                    onNavigate(
                      record.file_id,
                      record.sheet_id,
                      record.category,
                      record.extraction_id
                    )
                  }
                  color="#228BE6"
                  style={{
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                />
              </>
            )}
          </DescriptionStyle>
        );
      }
    }
  ];
  useDidUpdate(() => {
    setSeletectedExtraction([]);
  }, [debouncedSearch, selectedCategory, sortConfig]);

  useDidUpdate(() => {
    if (isOpen) {
      agentStore.setCurrentSubScreen(WEB_PAGE_STATE_ENUMS.AutomationExtractionDashboard);
    } else {
      agentStore.setCurrentSubScreen('');
      setSeletectedExtraction([]);
      setSortConfig({ key: '', direction: '' });
      setSearchTerm('');
      setSelectedCategory(null);
      setSelectedConfidenceRecord(null);
      setSelectedEvidenceVerdict(null);
      setResettingConfidenceBySheet({});
    }
  }, [isOpen]);

  useDidUpdate(() => {
    if (!Object.keys(resettingConfidenceBySheet).length) return;

    setResettingConfidenceBySheet(prev => {
      const next = { ...prev };
      let changed = false;
      Object.entries(prev).forEach(([sheetMappingId, previousTaskId]) => {
        const record = (issues ?? []).find(
          item => String(item.sheet_mapping_id) === String(sheetMappingId)
        );
        const currentTask = record?.latest_automation_task;
        const taskChanged = currentTask?.id !== previousTaskId;
        const isFinished = ['Completed', 'Failed', 'Cancelled'].includes(currentTask?.status);
        const hasNewConfidence = typeof record?.confidence_score === 'number';

        if (
          (taskChanged && isFinished && hasNewConfidence) ||
          currentTask?.status === 'Failed' ||
          currentTask?.status === 'Cancelled'
        ) {
          delete next[sheetMappingId];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [issues, resettingConfidenceBySheet]);

  const isSelectedExtractionsInProgress = useMemo(() => {
    return selectedExtraction.some(el => {
      const status = (issues ?? []).find(rec => rec.sheet_mapping_id === el)?.latest_automation_task
        ?.status;

      return status === 'In progress';
    });
  }, [selectedExtraction, issues]);

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={customStyles}>
      <Container>
        <CloseStyle as="button" type="button" aria-label="close" onClick={onClose}>
          <IoClose fontSize="18px" color="#868E96" />
        </CloseStyle>
        <Title>Extraction Issues</Title>
        <Flex gap={16} align="center" mb={16}>
          <TextInput
            placeholder="Search"
            leftSection={<TbSearch size={16} color="#ADB5BD" />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            size="sm"
            w={256}
          />
          <FilterDropdown
            label="Category"
            placeholder="All Categories"
            options={categoryOptions}
            value={selectedCategory}
            onChange={setSelectedCategory}
          />
        </Flex>
        <TableContainerStyle>
          <Table
            idPrefix="extraction-issues-table"
            columns={columns}
            hasIndex={false}
            data={tableData}
            isLoading={isLoading}
          />
        </TableContainerStyle>
        <FooterStyle>
          {isLatestPeriod && (
            <PermissionGuard allowed={canManageWorkflow}>
              <Button
                size="sm"
                leftIcon={<TbReload fontSize="16px" />}
                onClick={onAutoExtract}
                disabled={
                  automateReExtraction.isLoading ||
                  cancelAutomateExtraction.isLoading ||
                  selectedExtraction.length === 0
                }
                style={{
                  width: 159,
                  backgroundColor: '#228BE6',
                  border: '1px solid #228BE6',
                  '&:hover': {
                    backgroundColor: '#1c7ed6'
                  }
                }}
              >
                Re-Extract Again
              </Button>
            </PermissionGuard>
          )}

          {isLatestPeriod && selectedExtraction.length > 0 && isSelectedExtractionsInProgress && (
            <Button
              size="sm"
              leftIcon={<IoClose fontSize="16px" />}
              disabled={
                automateReExtraction.isLoading ||
                cancelAutomateExtraction.isLoading ||
                selectedExtraction.length === 0 ||
                !isSelectedExtractionsInProgress
              }
              onClick={onCancelExtraction}
              sx={{
                width: 159,
                backgroundColor: '#fff',
                border: '1px solid #E03131',
                color: '#E03131',

                '&:hover': {
                  backgroundColor: '#fff'
                }
              }}
            >
              Cancel Extraction
            </Button>
          )}
        </FooterStyle>
        <Tooltip
          id="warning-tooltip"
          data-testid="warning-tooltip"
          style={{
            background: '#868E96',
            padding: '8px'
          }}
        />
      </Container>
      <Modal
        isOpen={!!selectedConfidenceRecord}
        onRequestClose={() => {
          setSelectedEvidenceVerdict(null);
          setSelectedConfidenceRecord(null);
        }}
        style={detailsModalStyles}
      >
        <DetailsModalBodyStyle id="ai-extraction-details-modal">
          <CloseStyle
            id="ai-extraction-details-close"
            as="button"
            type="button"
            aria-label="close AI extraction details"
            onClick={() => {
              setSelectedEvidenceVerdict(null);
              setSelectedConfidenceRecord(null);
            }}
          >
            <IoClose fontSize="18px" color="#868E96" />
          </CloseStyle>
          <Title id="ai-extraction-details-title">AI Extraction Details</Title>
          <DetailsHeaderStyle id="ai-extraction-details-header">
            <ScoreRingStyle
              id="ai-extraction-confidence-ring"
              $score={selectedConfidenceScore ?? 0}
              $color={selectedScoreColor}
            >
              <Text
                id="ai-extraction-confidence-value"
                fz="24px"
                lh="28px"
                fw={800}
                c={selectedScoreColor}
              >
                {selectedConfidenceScore == null ? '-' : `${selectedConfidenceScore.toFixed(1)}%`}
              </Text>
              <Text
                id="ai-extraction-confidence-label"
                fz="11px"
                lh="14px"
                c="#868E96"
                fw={700}
                tt="uppercase"
              >
                Confidence
              </Text>
            </ScoreRingStyle>
            <div id="ai-extraction-details-overview">
              <StatusPillStyle id="ai-extraction-details-status-pill" $tone={selectedStatusTone}>
                {(displayedVerifierStatus || 'unverified').replace('_', ' ')}
              </StatusPillStyle>
              <Text
                id="ai-extraction-details-verifier-summary"
                fz="14px"
                lh="22px"
                c="#25262B"
                fw={500}
                mt={12}
              >
                {sanitizeVerifierText(selectedDetails.summary) ||
                  'The extracted values were checked against the source sheet and field-level evidence is shown below.'}
              </Text>
              <Text id="ai-extraction-details-source" fz="12px" lh="18px" c="#868E96" mt={10}>
                {selectedDetails.source?.file_name || selectedConfidenceRecord?.file_name || '-'} ·{' '}
                {selectedDetails.source?.sheet_name || selectedConfidenceRecord?.sheet_name || '-'}{' '}
                · {selectedDetails.source?.category || selectedConfidenceRecord?.category || '-'}
              </Text>
            </div>
          </DetailsHeaderStyle>
          <DetailGridStyle id="ai-extraction-details-metrics">
            <DetailCardStyle id="ai-extraction-details-status">
              <Text
                id="ai-extraction-details-status-label"
                fz="11px"
                c="#868E96"
                tt="uppercase"
                fw={700}
              >
                Status
              </Text>
              <Text
                id="ai-extraction-details-status-value"
                fz="18px"
                c="#25262B"
                fw={700}
                tt="capitalize"
              >
                {(displayedVerifierStatus || '-').replace('_', ' ')}
              </Text>
            </DetailCardStyle>
            <DetailCardStyle id="ai-extraction-details-matched">
              <Text
                id="ai-extraction-details-matched-label"
                fz="11px"
                c="#868E96"
                tt="uppercase"
                fw={700}
              >
                Matched Fields
              </Text>
              <Text id="ai-extraction-details-matched-value" fz="18px" c="#25262B" fw={700}>
                {selectedDetails.matched_fields ?? '-'} / {selectedDetails.checked_fields ?? '-'}
              </Text>
            </DetailCardStyle>
            <DetailCardStyle id="ai-extraction-details-review">
              <Text
                id="ai-extraction-details-review-label"
                fz="11px"
                c="#868E96"
                tt="uppercase"
                fw={700}
              >
                Needs Review
              </Text>
              <Text id="ai-extraction-details-review-value" fz="18px" c="#25262B" fw={700}>
                {selectedDetails.needs_review_fields ?? '-'}
              </Text>
            </DetailCardStyle>
          </DetailGridStyle>
          <SectionHeaderStyle id="ai-extraction-evidence-header">
            <Text id="ai-extraction-evidence-title" fz="15px" c="#25262B" fw={700}>
              Source Evidence
            </Text>
            <VerdictSummaryStyle id="ai-extraction-verdict-summary">
              {Object.entries(verdictCounts).map(([verdict, count]) => (
                <VerdictPillStyle
                  id={`ai-extraction-verdict-${verdict}`}
                  key={verdict}
                  $tone={verdictTones[verdict]}
                  $active={selectedEvidenceVerdict === verdict}
                  type="button"
                  onClick={() => {
                    setSelectedEvidenceVerdict(currentVerdict =>
                      currentVerdict === verdict ? null : verdict
                    );
                  }}
                >
                  {verdict.replace('_', ' ')}: {count}
                </VerdictPillStyle>
              ))}
            </VerdictSummaryStyle>
          </SectionHeaderStyle>
          {selectedEvidence.length > 0 && (
            <EvidenceFilterMetaStyle id="ai-extraction-evidence-filter-meta">
              {selectedEvidenceVerdict
                ? `Showing ${filteredEvidence.length} of ${selectedEvidence.length} ${selectedEvidenceVerdict.replace('_', ' ')} evidence rows. Click again to clear.`
                : `Showing all ${selectedEvidence.length} evidence rows.`}
            </EvidenceFilterMetaStyle>
          )}
          <EvidenceListStyle id="ai-extraction-evidence-list">
            {filteredEvidence.length > 0 ? (
              filteredEvidence.map(item => {
                const evidenceItemId = getEvidenceItemId(item);
                return (
                  <EvidenceItemStyle
                    id={`ai-extraction-evidence-${evidenceItemId}`}
                    key={evidenceItemId}
                    $tone={verdictTones[item.verdict]}
                  >
                    <EvidenceHeaderStyle id={`ai-extraction-evidence-field-${evidenceItemId}`}>
                      <Text
                        id={`ai-extraction-evidence-field-name-${evidenceItemId}`}
                        fz="14px"
                        c="#25262B"
                        fw={800}
                      >
                        {item.field}
                      </Text>
                      <EvidenceBadgeGroupStyle
                        id={`ai-extraction-evidence-badges-${evidenceItemId}`}
                      >
                        <EvidenceCategoryBadgeStyle
                          id={`ai-extraction-evidence-category-${evidenceItemId}`}
                        >
                          {item.extraction_category ||
                            selectedDetails.source?.category ||
                            selectedConfidenceRecord?.category ||
                            '-'}
                        </EvidenceCategoryBadgeStyle>
                        <VerdictPillStyle
                          id={`ai-extraction-evidence-verdict-${evidenceItemId}`}
                          $tone={verdictTones[item.verdict]}
                          type="button"
                          disabled
                        >
                          {(item.verdict || '-').replace('_', ' ')}
                        </VerdictPillStyle>
                        <FieldConfidenceBadgeStyle
                          id={`ai-extraction-evidence-field-confidence-${evidenceItemId}`}
                          $score={getFieldConfidenceScore(item)}
                        >
                          {getFieldConfidenceScore(item).toFixed(1)}% field confidence
                        </FieldConfidenceBadgeStyle>
                      </EvidenceBadgeGroupStyle>
                    </EvidenceHeaderStyle>
                    <div id={`ai-extraction-evidence-values-${evidenceItemId}`}>
                      <EvidenceValueStyle id={`ai-extraction-evidence-extracted-${evidenceItemId}`}>
                        <Text
                          id={`ai-extraction-evidence-extracted-label-${evidenceItemId}`}
                          fz="11px"
                          c="#868E96"
                          fw={700}
                        >
                          Extracted
                        </Text>
                        <Text
                          id={`ai-extraction-evidence-extracted-value-${evidenceItemId}`}
                          fz="12px"
                          c="#25262B"
                          fw={700}
                        >
                          {item.extracted_value || '-'}
                        </Text>
                      </EvidenceValueStyle>
                      <EvidenceValueStyle id={`ai-extraction-evidence-source-${evidenceItemId}`}>
                        <Text
                          id={`ai-extraction-evidence-source-label-${evidenceItemId}`}
                          fz="11px"
                          c="#868E96"
                          fw={700}
                        >
                          Source
                        </Text>
                        <div id={`ai-extraction-evidence-source-value-${evidenceItemId}`}>
                          {renderSourceReference(item, evidenceItemId)}
                        </div>
                      </EvidenceValueStyle>
                    </div>
                    <EvidenceReasonStyle id={`ai-extraction-evidence-reason-${evidenceItemId}`}>
                      {item.reason || '-'}
                    </EvidenceReasonStyle>
                  </EvidenceItemStyle>
                );
              })
            ) : (
              <Text id="ai-extraction-evidence-empty" fz="12px" c="#868E96">
                {selectedEvidenceVerdict
                  ? `No ${selectedEvidenceVerdict.replace('_', ' ')} source evidence rows.`
                  : 'No field-level source evidence was returned by the verifier.'}
              </Text>
            )}
          </EvidenceListStyle>
          {selectedConcerns.length > 0 && (
            <>
              <Text id="ai-extraction-concerns-title" fz="13px" c="#25262B" fw={600} mt={16}>
                Review Notes
              </Text>
              <EvidenceListStyle id="ai-extraction-concerns-list">
                {selectedConcerns.map((concern, index) => (
                  <NoteItemStyle id={`ai-extraction-concern-${index}`} key={`${concern}-${index}`}>
                    {concern}
                  </NoteItemStyle>
                ))}
              </EvidenceListStyle>
            </>
          )}
          {selectedMethodology.length > 0 && (
            <>
              <Text id="ai-extraction-methodology-title" fz="13px" c="#25262B" fw={600} mt={16}>
                How This Was Verified
              </Text>
              <EvidenceListStyle id="ai-extraction-methodology-list">
                {selectedMethodology.map((method, index) => (
                  <NoteItemStyle id={`ai-extraction-method-${index}`} key={`${method}-${index}`}>
                    {method}
                  </NoteItemStyle>
                ))}
              </EvidenceListStyle>
            </>
          )}
        </DetailsModalBodyStyle>
      </Modal>
    </Modal>
  );
};
